/**
 * Pms — Hospitality > PMS Integration (per-site).
 *
 * A hotel property's Property Management System (Opera/Mews/Cloudbeds/protel) connection.
 * Per-site (one property = one PMS). The API credential is write-only: it's stored as a
 * Vault reference and never returned — the form shows only whether one is set.
 */
import { useEffect, useState } from "react";
import { useSession } from "@/stores/session";
import { usePms, useSavePms } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";

const INPUT = "rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand";
const VENDORS = ["opera", "mews", "cloudbeds", "protel", "other"];

export default function Pms() {
  const siteId = useSession((s) => s.currentSiteId);
  const siteName = useSession((s) => s.currentSite()?.name);
  const { data, isLoading, error } = usePms(siteId);
  const save = useSavePms(siteId);

  const [enabled, setEnabled] = useState(false);
  const [vendor, setVendor] = useState("");
  const [host, setHost] = useState("");
  const [port, setPort] = useState("");
  const [siteCode, setSiteCode] = useState("");
  const [username, setUsername] = useState("");
  const [apiKey, setApiKey] = useState("");

  // Hydrate the form once the stored settings arrive (or reset for a new site).
  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled);
    setVendor(data.vendor ?? "");
    setHost(data.host ?? "");
    setPort(data.port != null ? String(data.port) : "");
    setSiteCode(data.site_code ?? "");
    setUsername(data.username ?? "");
    setApiKey("");
  }, [data]);

  if (!siteId) {
    return (
      <div>
        <PageHeader title="PMS Integration" subtitle="Connect a property's PMS" />
        <EmptyState title="Select a site" hint="PMS integration is configured per site (property). Pick a site in the scope bar." />
      </div>
    );
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    save.mutate({
      enabled,
      vendor: vendor || null,
      host: host.trim() || null,
      port: port ? Number(port) : null,
      site_code: siteCode.trim() || null,
      username: username.trim() || null,
      ...(apiKey ? { api_key: apiKey } : {}),
    });
  };

  return (
    <div className="max-w-xl">
      <PageHeader title="PMS Integration" subtitle={siteName ? `Property Management System for ${siteName}` : "Per-property PMS connection"} />

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading settings…</div>}

      {data && (
        <form onSubmit={submit} className="md-card flex flex-col gap-3 p-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span className="font-medium text-ink">Enable PMS integration</span>
          </label>

          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Vendor</span>
              <select value={vendor} onChange={(e) => setVendor(e.target.value)} className={INPUT}>
                <option value="">— select —</option>
                {VENDORS.map((v) => <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Property / site code</span>
              <input value={siteCode} onChange={(e) => setSiteCode(e.target.value)} placeholder="e.g. HOTEL01" className={INPUT} />
            </label>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <label className="col-span-2 flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Host / endpoint</span>
              <input value={host} onChange={(e) => setHost(e.target.value)} placeholder="pms.hotelbrand.com" className={`${INPUT} font-mono`} />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Port</span>
              <input inputMode="numeric" value={port} onChange={(e) => setPort(e.target.value.replace(/[^0-9]/g, ""))} placeholder="443" className={`${INPUT} font-mono`} />
            </label>
          </div>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-ink-muted">Username</span>
            <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="integration user" className={INPUT} />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-ink-muted">
              API key / secret {data.has_credential ? "(stored — leave blank to keep)" : ""}
            </span>
            <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              placeholder={data.has_credential ? "•••••• stored" : "PMS API key"} className={`${INPUT} font-mono`} />
          </label>

          {save.isError && <span className="text-xs text-critical">Could not save the PMS settings.</span>}
          {save.isSuccess && <span className="text-xs text-healthy">Saved.</span>}

          <div className="mt-1 flex items-center justify-end gap-2">
            <RoleGate min="org_admin" mode="disable" reason="Customer admin required to change PMS settings.">
              <Button variant="primary" type="submit" disabled={save.isPending}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </RoleGate>
          </div>
        </form>
      )}
    </div>
  );
}
